import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { siteToday } from '../../../lib/when';
import { supabase } from '../../../lib/supabase';
import { fetchAll } from '../../../lib/save';
import { useAuth } from '../../../components/AuthContext';
import { useToast } from '../../../components/ToastContext';
import { logAudit } from '../../../hooks/useAudit';
import PageBar from '../../../components/PageBar';
import ListPanel, { revealPanel } from '../../../components/ListPanel';
import WarehouseImport from './WarehouseImport';
import { WAREHOUSE_FIELDS } from './WarehouseFields';
import PageLoading from '../../../components/PageLoading';

// Admin -> Warehouses.
//
// The list on top, everything about the one you picked underneath. Built on the
// Claimsoft warehouse screen, because that is the shape of the data we import
// from - but with the two links Claimsoft does not have and we need:
//
//   Region             What Claimsoft calls "Assigned Billing Locations".
//                      A warehouse sits in one place, so this is one region,
//                      not a list. Left blank it is worked out from the name.
//   Referral source    The referral source this warehouse pairs with, one for
//                      one. The database refuses to let two warehouses claim
//                      the same one.
//
// Nobody is going to type nine hundred of these in. Download Template gives you
// a spreadsheet with every field as a column and every warehouse already in it;
// you fix it in Excel and import it back, and the links are made for you.

const BLANK = {
  name: '', warehouse_number: '', region_id: '', referral_source_id: '',
  manager_id: '', primary_warehouse_id: '',
  ship_to: false, transfer_only: false, is_inactive: false, allows_concurrent_counting: false,
  address1: '', address2: '', city: '', state: '', zip: '', country: 'United States',
  phone: '', phone_ext: '', phone2: '', phone2_ext: '', notes: '',
};

const SELECT_COLS = `
  id, name, display_name, region_prefix, warehouse_number, is_do_not_use,
  onhand_name, claimsoft_referral_name, account_edge_customer, account_edge_card_id,
  in_warehouse_list, in_referral_list, is_regional_main, region_name,
  region_id, referral_source_id, manager_id, primary_warehouse_id,
  ship_to, transfer_only, is_inactive, allows_concurrent_counting,
  address1, address2, city, state, zip, country,
  phone, phone_ext, phone2, phone2_ext, notes, warehouse_manager
`;

function Check() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M2 5l2.5 2.5 3.5-4" stroke="var(--on-action)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Field({ label, children, hint, span }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, gridColumn: span ? '1 / -1' : 'auto' }}>
      <span className="fld-label">{label}</span>
      {children}
      {hint && <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{hint}</span>}
    </label>
  );
}

function Tick({ label, checked, onChange, note }) {
  return (
    <label className="wh-tick">
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
      <span>
        {label}
        {note && <span className="wh-tick-note">{note}</span>}
      </span>
    </label>
  );
}

// A dropdown you can type into. Native <select> is fine for eight regions and
// hopeless for eight hundred referral sources, so anything long gets this.
function Lookup({ label, items, value, onChange, placeholder, hint, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const box = useRef(null);
  const chosen = items.find(i => i.id === value) || null;

  useEffect(() => {
    if (!open) return;
    const away = e => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hits = q ? items.filter(i => i.search.includes(q)) : items;
    return hits.slice(0, 60);
  }, [items, query]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }} ref={box}>
      <span className="fld-label">{label}</span>
      <div className="wh-lookup">
        <button
          type="button" className={'fld wh-lookup-btn' + (chosen ? '' : ' empty')}
          disabled={disabled}
          onClick={() => { setOpen(o => !o); setQuery(''); }}
        >
          <span className="wh-lookup-val">{chosen ? chosen.name : (placeholder || 'None')}</span>
          {chosen && chosen.tag && <span className="pick-tag">{chosen.tag}</span>}
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, opacity: 0.5 }}>
            <path d="M3 4.5 6 8l3-3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {open && (
          <div className="wh-lookup-menu">
            <input
              className="wh-lookup-search" autoFocus placeholder="Type to narrow the list..."
              value={query} onChange={e => setQuery(e.target.value)}
            />
            <div className="wh-lookup-list">
              <button type="button" className={'pick-item' + (!value ? ' on' : '')}
                onClick={() => { onChange(''); setOpen(false); }}>
                <span style={{ flex: 1, fontStyle: 'italic' }}>None</span>
              </button>
              {shown.map(i => (
                <button
                  type="button" key={i.id}
                  className={'pick-item' + (i.id === value ? ' on' : '')}
                  onClick={() => { onChange(i.id); setOpen(false); }}
                >
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.name}</span>
                  {i.tag && <span className="pick-tag">{i.tag}</span>}
                </button>
              ))}
              {shown.length === 0 && <div className="pick-empty">Nothing matches that.</div>}
              {!query && items.length > shown.length && (
                <div className="pick-empty">
                  Showing the first {shown.length} of {items.length}. Type to find the rest.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {hint && <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{hint}</span>}
    </div>
  );
}

// Tickable list, same one Users & Access uses.
function Picker({ label, items, selected, onChange, empty, hint }) {
  const allOn = items.length > 0 && selected.length === items.length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div className="pick-head">
        <span className="fld-label">{label}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="pick-count">{selected.length} of {items.length}</span>
          {items.length > 0 && (
            <button type="button" className="pick-all"
              onClick={() => onChange(allOn ? [] : items.map(i => i.id))}>
              {allOn ? 'Clear' : 'Select all'}
            </button>
          )}
        </span>
      </div>
      <div className="pick">
        {items.length === 0 ? (
          <div className="pick-empty">{empty || 'Nothing to choose from yet.'}</div>
        ) : items.map(i => {
          const on = selected.includes(i.id);
          return (
            <button type="button" key={i.id} className={'pick-item' + (on ? ' on' : '')}
              onClick={() => onChange(on ? selected.filter(x => x !== i.id) : [...selected, i.id])}>
              <span className="pick-box">{on && <Check />}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.name}</span>
              {i.tag && <span className="pick-tag">{i.tag}</span>}
            </button>
          );
        })}
      </div>
      {hint && <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{hint}</span>}
    </div>
  );
}

export default function Warehouses() {
  const { profile } = useAuth();
  const toast = useToast();
  const canEdit = profile?.role === 'admin';

  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');

  const [regions, setRegions]   = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [people, setPeople]     = useState([]);
  // Everybody in the site, switched off included, for the export and the
  // import: question 16, 18 September 2026. `people` stays the active ones,
  // which is who the pickers offer.
  const [allPeople, setAllPeople] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm]         = useState(null);
  const [assigned, setAssigned] = useState([]);
  /*
    **Whose people are on screen, once they have been read**, 18 September
    2026 (the audit after the rebrand, issue 14). Picking a location emptied its
    people and read them back without waiting, and Save was live the whole time,
    so pressing it in that moment unassigned everybody. Save waits for this to
    name the location selected, and a late answer for another one is ignored.
  */
  const [assignedFor, setAssignedFor] = useState(null);
  const latestRow = useRef(null);
  const [saving, setSaving]     = useState(false);
  const [importing, setImporting] = useState(false);

  const detailRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [w, r, rs, p] = await Promise.all([
      // A page at a time (the audit after the rebrand, issue 22).
      fetchAll(() => supabase.from('warehouses').select(SELECT_COLS).order('name').order('id')),
      supabase.from('regions').select('id, name').order('name'),
      supabase.from('referral_sources').select('id, name, display_name, region_prefix').order('name'),
      supabase.rpc('site_users'),
    ]);
    if (w.error) toast.error('Could not load warehouses: ' + w.error.message);
    setRows(w.data || []);
    setRegions(r.data || []);
    setReferrals(rs.data || []);
    setPeople((p.data || []).filter(u => u.is_active));
    setAllPeople(p.data || []);
    setLoading(false);
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const regionName = useCallback(
    id => regions.find(r => r.id === id)?.name || '',
    [regions],
  );

  // Options for the searchable dropdowns. `search` is what typing matches on,
  // so the prefix is findable even though the name shown has it stripped off.
  const referralOptions = useMemo(() => referrals.map(r => ({
    id: r.id, name: r.display_name || r.name, tag: r.region_prefix,
    search: (r.name + ' ' + (r.display_name || '')).toLowerCase(),
  })), [referrals]);

  // Names only. An email address next to every name is noise when what you are
  // doing is picking a person - typing one still finds them.
  const personOptions = useMemo(() => people.map(u => ({
    id: u.id, name: u.full_name,
    search: ((u.full_name || '') + ' ' + (u.email || '')).toLowerCase(),
  })), [people]);

  const hubOptions = useMemo(() => rows
    .filter(w => w.id !== selectedId)
    .map(w => ({
      id: w.id, name: w.display_name || w.name, tag: w.region_prefix,
      search: (w.name + ' ' + (w.warehouse_number || '')).toLowerCase(),
    })), [rows, selectedId]);

  const peopleItems = useMemo(
    () => people.map(u => ({ id: u.id, name: u.full_name })),
    [people],
  );

  // A referral source already claimed by another warehouse cannot be picked
  // here - the database would refuse it, so it should not be offered.
  const freeReferralOptions = useMemo(() => {
    const taken = new Set(
      rows.filter(w => w.referral_source_id && w.id !== selectedId).map(w => w.referral_source_id),
    );
    return referralOptions.filter(o => !taken.has(o.id));
  }, [referralOptions, rows, selectedId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(w => {
      if (statusFilter === 'active'   &&  w.is_inactive) return false;
      if (statusFilter === 'inactive' && !w.is_inactive) return false;
      if (regionFilter === 'none' && w.region_id) return false;
      if (regionFilter !== 'all' && regionFilter !== 'none' && w.region_id !== regionFilter) return false;
      if (!q) return true;
      return [w.name, w.display_name, w.warehouse_number, w.city].some(
        v => (v || '').toLowerCase().includes(q),
      );
    });
  }, [rows, search, regionFilter, statusFilter]);

  function toForm(w) {
    const out = { ...BLANK };
    Object.keys(BLANK).forEach(k => { if (w[k] !== null && w[k] !== undefined) out[k] = w[k]; });
    return out;
  }

  const selectRow = useCallback(async w => {
    setCreating(false);
    setSelectedId(w.id);
    setForm(toForm(w));
    setAssigned([]);
    setAssignedFor(null);
    latestRow.current = w.id;
    const { data, error } = await supabase.from('user_warehouses').select('user_id').eq('warehouse_id', w.id);
    if (latestRow.current !== w.id) return;   // another location was picked meanwhile
    if (error) {
      toast.error('Could not read who covers this location, so Save is off until it loads. Pick it again to retry.');
      return;
    }
    setAssigned((data || []).map(r => r.user_id));
    setAssignedFor(w.id);
    // Below 1024 the panel sits above the list, so bring it into view. On a
    // desk it is already beside the list and revealPanel leaves it alone.
    requestAnimationFrame(() => revealPanel(detailRef.current));
  }, [toast]);

  function startNew() {
    setCreating(true);
    setSelectedId(null);
    setForm({ ...BLANK });
    setAssigned([]);
    requestAnimationFrame(() => revealPanel(detailRef.current));
  }

  async function save() {
    if (!form) return;
    if (!creating && assignedFor !== selectedId) return;   // its people are not on screen yet (issue 14)
    if (!form.name.trim()) { toast.error('A warehouse needs a name.'); return; }
    setSaving(true);

    // Empty dropdowns come through as '' and the database wants nothing at all.
    const payload = { ...form, name: form.name.trim() };
    ['region_id', 'referral_source_id', 'manager_id', 'primary_warehouse_id',
     'warehouse_number', 'address1', 'address2', 'city', 'state', 'zip',
     'country', 'phone', 'phone_ext', 'phone2', 'phone2_ext', 'notes',
    ].forEach(k => { if (payload[k] === '') payload[k] = null; });

    let id = selectedId;
    if (creating) {
      const { data, error } = await supabase.from('warehouses').insert(payload).select('id').single();
      if (error) { toast.error(friendly(error)); setSaving(false); return; }
      id = data.id;
    } else {
      const { error } = await supabase.from('warehouses').update(payload).eq('id', selectedId);
      if (error) { toast.error(friendly(error)); setSaving(false); return; }
    }

    const ok = await syncAssigned(id, assigned);
    if (!ok) { setSaving(false); return; }

    await logAudit(profile, creating ? 'WAREHOUSE_CREATED' : 'WAREHOUSE_UPDATED',
      'warehouse', { target_name: payload.name });
    toast.success(creating ? 'Warehouse added' : 'Saved');
    setSaving(false);
    setCreating(false);
    await load();
    setSelectedId(id);
    // What is on screen is exactly what was just saved.
    latestRow.current = id;
    setAssignedFor(id);
  }

  // Replace the list of people who can reach this warehouse with the ticked one.
  async function syncAssigned(warehouseId, userIds) {
    const { data: existing, error: readErr } = await supabase
      .from('user_warehouses').select('user_id').eq('warehouse_id', warehouseId);
    if (readErr) { toast.error('Could not read who is assigned: ' + readErr.message); return false; }

    const before = new Set((existing || []).map(r => r.user_id));
    const after  = new Set(userIds);
    const add    = userIds.filter(u => !before.has(u));
    const remove = [...before].filter(u => !after.has(u));

    if (add.length) {
      const { error } = await supabase.from('user_warehouses')
        .insert(add.map(user_id => ({ user_id, warehouse_id: warehouseId })));
      if (error) { toast.error('Could not assign people: ' + error.message); return false; }
    }
    if (remove.length) {
      const { error } = await supabase.from('user_warehouses')
        .delete().eq('warehouse_id', warehouseId).in('user_id', remove);
      if (error) { toast.error('Could not unassign people: ' + error.message); return false; }
    }
    return true;
  }

  function friendly(error) {
    if (error.message?.includes('warehouses_one_referral_source')) {
      return 'That referral source is already paired with another warehouse.';
    }
    if (error.message?.includes('warehouses_one_number')) {
      return 'Another warehouse already uses that number.';
    }
    return 'Could not save: ' + error.message;
  }

  async function remove() {
    if (!selectedId) return;
    const w = rows.find(r => r.id === selectedId);
    if (!window.confirm(`Delete ${w?.name}? If it has ever been counted, mark it Inactive instead.`)) return;
    const { error } = await supabase.from('warehouses').delete().eq('id', selectedId);
    if (error) { toast.error('Could not delete: ' + error.message); return; }
    await logAudit(profile, 'WAREHOUSE_DELETED', 'warehouse', { target_name: w?.name });
    toast.success('Deleted');
    setSelectedId(null); setForm(null);
    load();
  }

  // -- The template --
  // Every field as a column, every warehouse already filled in, plus one
  // example row showing the format. Fix it in Excel, import it back.
  async function downloadTemplate(withData) {
    const { default: Papa } = await import('papaparse');
    const cols = WAREHOUSE_FIELDS.map(f => f.column);

    const example = {};
    WAREHOUSE_FIELDS.forEach(f => { example[f.column] = f.example ?? ''; });

    let body = [];
    if (withData) {
      const byId = {
        region:   Object.fromEntries(regions.map(r => [r.id, r.name])),
        referral: Object.fromEntries(referrals.map(r => [r.id, r.name])),
        person:   Object.fromEntries(allPeople.map(p => [p.id, p.email || p.full_name])),
        wh:       Object.fromEntries(rows.map(w => [w.id, w.name])),
      };
      const assignedByWarehouse = await loadAllAssignments();
      /*
        **Every location, whatever is on screen**, and **switched-off people
        included**: the owner's answers of 18 September 2026 (question 16).
        It exported only what the search and filters left showing, and it left
        switched-off people out, so the same file loaded back took their
        locations away from them.
      */
      body = rows.map(w => {
        const line = {};
        WAREHOUSE_FIELDS.forEach(f => {
          const v = w[f.key];
          if (f.kind === 'yesno')          line[f.column] = v ? 'Yes' : 'No';
          else if (f.kind === 'region')    line[f.column] = byId.region[v] || '';
          else if (f.kind === 'referral')  line[f.column] = byId.referral[v] || '';
          else if (f.kind === 'person')    line[f.column] = byId.person[v] || w.warehouse_manager || '';
          else if (f.kind === 'warehouse') line[f.column] = byId.wh[v] || '';
          else if (f.kind === 'people')    line[f.column] = (assignedByWarehouse[w.id] || []).join('; ');
          else                             line[f.column] = v ?? '';
        });
        return line;
      });
    } else {
      body = [example];
    }

    const csv = Papa.unparse({ fields: cols, data: body.map(r => cols.map(c => r[c])) });
    // The site's day, not Greenwich's (issue 23, 18 September 2026).
    const stamp = siteToday();
    const name = withData ? `OnHand-Warehouses-${stamp}.csv` : 'OnHand-Warehouse-Template.csv';
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(withData ? `${body.length} warehouses downloaded` : 'Template downloaded');
  }

  async function loadAllAssignments() {
    const { data } = await fetchAll(() => supabase.from('user_warehouses').select('warehouse_id, user_id')
      .order('warehouse_id').order('user_id'));
    const email = Object.fromEntries(allPeople.map(p => [p.id, p.email || p.full_name]));
    const out = {};
    (data || []).forEach(r => {
      if (!email[r.user_id]) return;
      (out[r.warehouse_id] ||= []).push(email[r.user_id]);
    });
    return out;
  }

  const selected = rows.find(r => r.id === selectedId) || null;

  return (
    <>
      <PageBar
        title="Warehouses"
        subtitle="Where stock lives, and who can reach it"
        controls={
          <>
            <input className="tb-search"
              placeholder="Search name, number or city..."
              value={search} onChange={e => setSearch(e.target.value)} />
            {<select className={'tb-select' + (regionFilter !== 'all' ? ' set' : '')}
              value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
              <option value="all">All regions</option>
              {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              <option value="none">No region set</option>
            </select>}
            {<select className={'tb-select' + (statusFilter !== 'active' ? ' set' : '')}
              value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>}
          </>
        }
        meta={filtered.length + ' of ' + rows.length + ' warehouses'}
        actions={(
          <>
            <button className="tb-btn tb-btn-ghost" onClick={() => downloadTemplate(false)}>Download template</button>
            <button className="tb-btn tb-btn-ghost" onClick={() => downloadTemplate(true)}>Export current</button>
            {canEdit && <button className="tb-btn tb-btn-ghost" onClick={() => setImporting(true)}>Import</button>}
            {canEdit && <button className="tb-btn tb-btn-primary" onClick={startNew}>+ New Warehouse</button>}
          </>
        )}
      />

      {/* **The place mapping moved out to Setup on 6 October 2026**, on the
          owner's word: the mapping tools are for setting the system up and
          then go away, so they live together in one area he alone sees and
          can switch off. It had been a second view of this page since 4
          October. */}

      <ListPanel
        selected={selectedId}
        listWidth="minmax(280px, 360px)"
        empty="Pick a warehouse to see its details, or add a new one."
        list={
          loading ? (
            <PageLoading rows={8} />
          ) : filtered.length === 0 ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, fontStyle: 'italic' }}>
              {rows.length === 0 ? 'No warehouses yet. Add one, or import a file.' : 'Nothing matches those filters.'}
            </div>
          ) : filtered.map(w => (
            <button key={w.id} title={w.name} className={'ua-row' + (w.id === selectedId ? ' on' : '')} onClick={() => selectRow(w)}>
              <span className="ua-row-main">
                <span className="ua-row-name">
                  {w.region_prefix && <span className="pick-tag">{w.region_prefix}</span>}
                  <span className="wh-row-title">{w.display_name || w.name}</span>
                  {w.is_inactive && <span className="ua-inactive">Inactive</span>}
                </span>
                <span className="ua-row-email">
                  {[w.warehouse_number, regionName(w.region_id) || 'no region', w.city]
                    .filter(Boolean).join('  -  ')}
                </span>
              </span>
              <span className="ua-row-side">
                {w.referral_source_id
                  ? <span className="wh-paired" title="Paired with a referral source">Paired</span>
                  : <span className="wh-unpaired">Not paired</span>}
              </span>
            </button>
          ))
        }
        panel={!form ? null : (
          <div className="card ua-detail wh-detail" ref={detailRef}>
            <div className="ua-detail-body">
              <div className="ua-detail-head">
                <div style={{ minWidth: 0 }}>
                  {/* **The OnHand name leads**, from 4 October 2026. A place has
                      a different name in every system and this is the one OnHand
                      calls it by; the ClaimSoft name is one of the four below. */}
                  <div className="ua-detail-name">
                    {creating ? 'New warehouse' : (form.onhand_name || form.name || '-')}
                  </div>
                  <div className="ua-detail-sub">
                    {creating
                      ? 'The region is worked out from the name prefix if you leave it blank.'
                      : [form.region_name,
                         form.warehouse_number ? 'Number ' + form.warehouse_number : null,
                         form.is_regional_main ? 'Regional main' : null,
                        ].filter(Boolean).join('  -  ') || 'No warehouse number set'}
                  </div>
                </div>
                <Tick label={form.is_inactive ? 'Inactive' : 'Active'}
                  checked={!form.is_inactive}
                  onChange={v => setForm(f => ({ ...f, is_inactive: !v }))} />
              </div>

              {/* -- The essentials -- */}
              {/* Not the even grid the rest of the form uses. A warehouse name
                  runs to "SAT-CSR WOUND CARE-NEW BRAUNFELS"; a number never
                  passes DFW-999. Giving them equal width suits neither. */}
              {/*
                WHAT EACH SYSTEM CALLS THIS PLACE, 4 October 2026, on his
                instruction: *"Where we have a OnHand standard name, then
                claimsoft referral source name, claimsoft warehouse name,
                account edge customer name."*

                **The Account Edge pair is set in Setup -> Place Mapping**, not
                here, because that is the one that has to be worked through in
                bulk. (It was the Mapping tab on this page until 6 October 2026.)
                It is shown here so a warehouse's whole identity is in one place.
              */}
              {!creating && (
                <div className="wh-grid" style={{ marginBottom: 4 }}>
                  <Field label="OnHand name" hint="What OnHand calls this place. It is the one name that never changes.">
                    <input className="fld" value={form.onhand_name || ''}
                      onChange={e => setForm(f => ({ ...f, onhand_name: e.target.value }))} />
                  </Field>
                  <Field label="Region">
                    <input className="fld" value={form.region_name || form.region_prefix || ''} readOnly />
                  </Field>
                  <Field label="ClaimSoft warehouse"
                    hint={form.in_warehouse_list ? null : 'Not on the ClaimSoft warehouse list.'}>
                    <input className="fld" value={form.in_warehouse_list ? (form.name || '') : ''}
                      placeholder="not on this list" readOnly />
                  </Field>
                  <Field label="ClaimSoft referral source"
                    hint={form.in_referral_list ? null : 'Not on the ClaimSoft referral source list.'}>
                    <input className="fld" value={form.in_referral_list ? (form.claimsoft_referral_name || '') : ''}
                      placeholder="not on this list" readOnly />
                  </Field>
                  <Field label="Account Edge customer" hint="Set in Setup, Place Mapping.">
                    <input className="fld" value={form.account_edge_customer || ''}
                      placeholder="not set" readOnly />
                  </Field>
                  <Field label="Account Edge card ID" hint="What Account Edge matches on.">
                    <input className="fld" value={form.account_edge_card_id || ''}
                      placeholder="not set" readOnly />
                  </Field>
                </div>
              )}

              <div className="wh-headline">
                <Field label="Warehouse name" hint="As it is in Claimsoft, prefix and all.">
                  <input className="fld" value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </Field>
                <Field label="Number">
                  <input className="fld" value={form.warehouse_number}
                    onChange={e => setForm(f => ({ ...f, warehouse_number: e.target.value }))} />
                </Field>
              </div>

              <div className="ua-fields">
                <Field label="Region" hint="Claimsoft calls this Assigned Billing Location.">
                  <select className="fld-sel" value={form.region_id || ''}
                    onChange={e => setForm(f => ({ ...f, region_id: e.target.value }))}>
                    <option value="">Not set</option>
                    {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </Field>
                <Lookup
                  label="Paired referral source"
                  items={freeReferralOptions}
                  value={form.referral_source_id}
                  onChange={v => setForm(f => ({ ...f, referral_source_id: v }))}
                  placeholder="Not paired"
                  hint="One warehouse to one referral source."
                />
              </div>

              <div className="wh-ticks">
                <Tick label="Ship to warehouse address" checked={form.ship_to}
                  onChange={v => setForm(f => ({ ...f, ship_to: v }))} />
                <Tick label="Transfer only" checked={form.transfer_only}
                  onChange={v => setForm(f => ({ ...f, transfer_only: v }))}
                  note="Stock moves in and out by transfer, never ordered directly." />
                <Tick label="Several people count it at once" checked={form.allows_concurrent_counting}
                  onChange={v => setForm(f => ({ ...f, allows_concurrent_counting: v }))}
                  note="For a regional hub a team counts together. Everywhere else one person holds the sheet at a time, so two people cannot overwrite each other." />
              </div>

              <Field label="Notes" span>
                <textarea className="fld" rows={2} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </Field>

              {/* -- Where it is, and who runs it -- */}
              <div className="ua-section">
                <div className="ua-section-head">
                  <span className="ua-section-title">Contact information</span>
                </div>
                <div className="ua-fields">
                  <Field label="Address line 1">
                    <input className="fld" value={form.address1}
                      onChange={e => setForm(f => ({ ...f, address1: e.target.value }))} />
                  </Field>
                  <Field label="Address line 2">
                    <input className="fld" value={form.address2}
                      onChange={e => setForm(f => ({ ...f, address2: e.target.value }))} />
                  </Field>
                  <Field label="City">
                    <input className="fld" value={form.city}
                      onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                  </Field>
                  <Field label="State">
                    <input className="fld" value={form.state}
                      onChange={e => setForm(f => ({ ...f, state: e.target.value }))} />
                  </Field>
                  <Field label="ZIP">
                    <input className="fld" value={form.zip}
                      onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} />
                  </Field>
                  <Field label="Country">
                    <input className="fld" value={form.country}
                      onChange={e => setForm(f => ({ ...f, country: e.target.value }))} />
                  </Field>
                  <Field label="Phone">
                    <input className="fld" value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                  </Field>
                  <Field label="Phone extension">
                    <input className="fld" value={form.phone_ext}
                      onChange={e => setForm(f => ({ ...f, phone_ext: e.target.value }))} />
                  </Field>
                  <Lookup
                    label="Warehouse manager" items={personOptions}
                    value={form.manager_id}
                    onChange={v => setForm(f => ({ ...f, manager_id: v }))}
                    placeholder="Nobody assigned"
                    hint={!form.manager_id && selected?.warehouse_manager
                      ? 'Claimsoft says "' + selected.warehouse_manager + '", and no matching account was found.'
                      : undefined}
                  />
                  <Lookup
                    label="Primary warehouse" items={hubOptions}
                    value={form.primary_warehouse_id}
                    onChange={v => setForm(f => ({ ...f, primary_warehouse_id: v }))}
                    placeholder="Top level"
                    hint="The main warehouse this one sits under."
                  />
                </div>
              </div>

              {/* -- Who can reach it -- */}
              <div className="ua-section">
                <div className="ua-section-head">
                  <span className="ua-section-title">People assigned to this warehouse</span>
                  <span className="ua-section-note">
                    When a count cycle opens, these are the people who get its count sheet.
                    The same list can be set from a person's side in Settings &gt; Users &amp; Access.
                  </span>
                </div>
                <div className="ua-pickers">
                  <Picker label="People" items={peopleItems} selected={assigned}
                    onChange={setAssigned} empty="Nobody has access to this site yet." />
                </div>
              </div>

              <div className="ua-actions">
                {!creating && canEdit && <button className="tb-btn tb-btn-danger" onClick={remove}>Delete</button>}
                <span style={{ flex: 1 }} />
                <button className="tb-btn tb-btn-ghost"
                  onClick={() => { setCreating(false); setForm(null); setSelectedId(null); }}>
                  {creating ? 'Cancel' : 'Close'}
                </button>
                {canEdit && (
                  <button className="tb-btn tb-btn-primary" onClick={save} disabled={saving || (!creating && assignedFor !== selectedId)}>
                    {saving ? 'Saving...' : creating ? 'Create warehouse' : 'Save changes'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      />

      {importing && (
        <WarehouseImport
          onClose={() => setImporting(false)}
          onDone={() => { setImporting(false); load(); }}
          regions={regions}
          referrals={referrals}
          // Everybody, so a switched-off person named in the file is matched
          // and kept rather than dropped (question 16).
          people={allPeople}
          warehouses={rows}
        />
      )}
    </>
  );
}
